import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { MAILBOX_FEATURE_SET } from '@stitch/pbx-adapter/catalog';
import { api, type Directory, type InviteResult, type MemberView, type MessagingSeats, type RolePermission, type RolePermissions, type RoleView, type SeatRequestView } from '../../api/client';
import { DestinationPicker } from '../../components/pickers';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader, TextAreaField } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { dateOnly, monthDay, plural, type Tone } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

const ROLE = { admin: 'Admin', user: 'Their own phone' } as const;

// --- Messaging-only people: no extension, Messages only; each company's plan includes so many ---------

/** "3 of 5 messaging-only people". */
export const seatsText = (s: MessagingSeats) => `${s.used} of ${s.allowance} messaging-only ${s.allowance === 1 ? 'person' : 'people'}`;

/** What to say when they're all taken (the API says the same). */
export function seatsFullText(s: MessagingSeats, staff: boolean): string {
  const has = s.allowance === 0 ? 'doesn’t include messaging-only people' : `includes ${s.allowance} messaging-only ${s.allowance === 1 ? 'person' : 'people'}`;
  return staff ? `This company’s plan ${has}. Raise it under Stitch Admin → Customers.` : `Your plan ${has}; ask Stitch for ${s.allowance === 0 ? 'some' : 'more'}.`;
}

/** What someone can do here, for the Access column: their role, and their phone (or that they have none). */
function accessLabel(p: { role: 'admin' | 'user' | 'staff' | null; extension: string | null; messagingOnly?: boolean | undefined }, roleName: string | undefined): ReactNode {
  if (p.messagingOnly) {
    return (
      <>
        Messaging only <span className="muted">(no phone)</span>
      </>
    );
  }
  return (
    <>
      {p.role === 'admin' ? ROLE.admin : (roleName ?? ROLE.user)}
      {p.extension ? <span className="muted"> · ext {p.extension}</span> : p.role === 'user' ? <span className="muted"> · no phone</span> : null}
    </>
  );
}

/** "Requested 5 more on Oct 28 · waiting" (or done, or declined), and what it means. */
export function seatRequestText(r: SeatRequestView): { line: string; tone: Tone; detail: string } {
  const asked = `Requested ${r.more} more on ${monthDay(r.askedAt)}`;
  if (r.status === 'waiting') return { line: `${asked} · waiting`, tone: 'brand', detail: 'Stitch raises your plan, or says why not.' };
  if (r.status === 'done') return { line: `${asked} · done`, tone: 'ok', detail: `Your plan includes ${plural(r.allowanceAfter ?? 0, 'messaging-only person', 'messaging-only people')} now.` };
  return { line: `${asked} · declined`, tone: 'warn', detail: r.reason ? `Stitch said: “${r.reason}”` : '' };
}

function SeatsLine({ seats, staff, request, onAsk }: { seats: MessagingSeats; staff: boolean; request: SeatRequestView | null | undefined; onAsk: () => void }) {
  const full = seats.used >= seats.allowance;
  const said = request ? seatRequestText(request) : null;
  return (
    <div className="seats-line">
      <span className="seats-now" role="status">
        <Icon name="chat" size={16} />
        <strong>{seatsText(seats)}</strong>
        <span className="muted">{full ? seatsFullText(seats, staff) : 'People without an extension use Messages only. Admins don’t count.'}</span>
        {full && (
          <Chip small tone="warn">
            All taken
          </Chip>
        )}
      </span>
      {/* Admins ask Stitch for more; Stitch staff raise it themselves (Stitch Admin → Customers). */}
      {!staff && request?.status !== 'waiting' && (
        <Button size="small" icon="plus" className="seats-ask" onClick={onAsk}>
          Request more
        </Button>
      )}
      {said && (
        <span className="seats-request" role="status">
          <Chip small tone={said.tone}>
            {said.line}
          </Chip>
          {said.detail && <span className="muted">{said.detail}</span>}
        </span>
      )}
    </div>
  );
}

/** How many more messaging-only people to ask Stitch for, and why (optional). */
function RequestSeatsDialog({ seats, onClose, onAsk }: { seats: MessagingSeats; onClose: () => void; onAsk: (more: number, note: string) => Promise<void> }) {
  const [more, setMore] = useState('5');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const n = Number(more);
  const bad = !/^\d{1,4}$/.test(more) || n < 1 || n > 1000 ? 'A number from 1 to 1,000.' : '';
  return (
    <Dialog
      open
      onClose={onClose}
      title="Request more messaging-only people"
      subtitle={`Your plan includes ${plural(seats.allowance, 'messaging-only person', 'messaging-only people')}; ${seats.used} ${seats.used === 1 ? 'is' : 'are'} taken. Stitch looks at your request and raises your plan, or says why not.`}
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={Boolean(bad) || busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await onAsk(n, note.trim());
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
                setBusy(false);
              }
            }}
          >
            Send request
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="How many more" inputMode="numeric" value={more} data-autofocus onChange={(e) => setMore(e.target.value.replace(/\D/g, ''))} error={bad || undefined} hint={!bad ? `${seats.allowance + n} in all.` : undefined} />
        <TextAreaField label="Note for Stitch (optional)" rows={3} maxLength={500} value={note} placeholder="Who they’re for, or when you need them" onChange={(e) => setNote(e.target.value)} />
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}

/** A link to share by hand: copied, or selected for copying where the clipboard isn't allowed. */
export function ShareLink({ result }: { result: InviteResult }) {
  const [copied, setCopied] = useState(false);
  const link = result.link;
  if (!link) {
    // Emailed: the link goes only to them, so nobody else can use it.
    return (
      <Notice tone="ok" title={`Invitation emailed to ${result.invite.email}`}>
        The link in it works once, for 7 days. If it doesn’t arrive, check their spam folder, then send a new invitation.
      </Notice>
    );
  }
  return (
    <Notice tone="brand" title="Send them this link">
      <span>Email isn’t set up for Stitch yet, so send this link to them yourself. It works once, for 7 days.</span>
      {/* The link signs someone in: never shown to anyone helping over live help. */}
      <div className="share-link" data-private="">
        <input className="input mono" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Invitation link" />
        <Button
          size="small"
          icon={copied ? 'check' : 'copy'}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
            } catch {
              /* select it instead */
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </Notice>
  );
}

/** A password reset link to pass on, when Stitch can't email it (3046). */
function ResetLink({ link, minutes }: { link: string; minutes: number }) {
  const [copied, setCopied] = useState(false);
  return (
    <Notice tone="brand" title="Send them this link">
      <span>
        Email isn’t set up for Stitch yet, so send this link to them yourself. It works once, for {minutes} minutes, and choosing a new password with it signs them out everywhere
        else.
      </span>
      {/* The link resets someone's password: never shown to anyone helping over live help. */}
      <div className="share-link" data-private="">
        <input className="input mono" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Password reset link" />
        <Button
          size="small"
          icon={copied ? 'check' : 'copy'}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
            } catch {
              /* select it instead */
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </Notice>
  );
}

/** Who can sign in to this company's portal, and as what. */
export function TenantTeam() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const { state } = useAuth();
  const access = useData(() => api.access(d), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const [inviting, setInviting] = useState(false);
  const [inviteKey, setInviteKey] = useState(0);
  const [result, setResult] = useState<InviteResult | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<MemberView | null>(null);
  /** Remove access asks first (3045): one press of the bin used to sign someone out for good. */
  const [removing, setRemoving] = useState<MemberView | null>(null);
  /** A password reset made here (3046): emailed, or the link to pass on when Stitch can't email. */
  const [reset, setReset] = useState<{ member: MemberView; link: string | null; minutes: number } | null>(null);
  const [notice, setNotice] = useState('');
  const [roleEdit, setRoleEdit] = useState<RoleView | 'new' | null>(null);
  const [asking, setAsking] = useState(false);
  const meId = state?.user?.id;
  const staff = Boolean(state?.user?.staff);
  const queues = (dir.data?.entries ?? []).filter((e) => e.kind === 'callQueue').map((e) => ({ extension: e.extension, name: e.name || e.extension }));
  const queueName = (q: string) => queues.find((x) => x.extension === q)?.name ?? q;
  const roles = access.data?.roles ?? [];
  const roleName = (id: string | null | undefined) => roles.find((r) => r.id === id)?.name;

  async function act(fn: () => Promise<unknown>) {
    setError('');
    try {
      await fn();
      access.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const data = access.data;
  return (
    <>
      <PageHeader
        eyebrow={data ? `${plural(data.members.length, 'person', 'people')} can sign in` : d}
        title="Portal access"
        actions={
          <Button
            variant="primary"
            icon="plus"
            disabled={!data}
            onClick={() => {
              setInviteKey((k) => k + 1);
              setResult(null);
              setInviting(true);
            }}
          >
            Invite someone
          </Button>
        }
      />
      <div className="content">
        <Notice title="Admins, end users and your own roles">
          Admins manage the whole company here. End users see only their own phone: its keys, their call queues and their calls. Messaging-only people have no phone: they use Messages. Your own roles add more for some people, such as call recordings for a few queues.
        </Notice>
        {data?.seats && <SeatsLine seats={data.seats} staff={staff} request={data.seatRequest} onAsk={() => setAsking(true)} />}
        {result && <ShareLink result={result} />}
        {error && <Notice tone="crit">{error}</Notice>}
        {notice && <Notice tone="ok">{notice}</Notice>}
        <Card>
          {access.error ? (
            <LoadError error={access.error} retry={access.reload} />
          ) : !data ? (
            <Loading label="Loading" />
          ) : data.members.length === 0 && data.invites.length === 0 ? (
            <Empty title="Nobody can sign in yet">Invite the person who will manage the phones.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Person</th>
                    <th scope="col">Access</th>
                    <th scope="col" className="hide-sm">
                      Last signed in
                    </th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <span className="cell-stack">
                          <span>
                            {m.name || m.email} {m.id === meId && <Chip small>You</Chip>}
                          </span>
                          <small className="muted">{m.email}</small>
                        </span>
                      </td>
                      <td>
                        <span className="cell-stack">
                          <span>{accessLabel(m, roleName(m.roleId))}</span>
                          {m.messagingOnly && (
                            <button type="button" className="link-btn small" onClick={() => setEditing(m)}>
                              Give them a phone
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="hide-sm">{m.lastSignInAt ? dateOnly(m.lastSignInAt) : <span className="muted">Not yet</span>}</td>
                      <td>
                        <div className="row-actions">
                          <button type="button" className="icon-btn" aria-label={`Change ${m.name || m.email}’s access`} title="Change access" onClick={() => setEditing(m)}>
                            <Icon name="edit" size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            aria-label={`Reset ${m.name || m.email}’s password`}
                            title="Reset their password"
                            onClick={() =>
                              void act(async () => {
                                setNotice('');
                                const r = await api.resetLink(d, m.id);
                                if (r.emailed) setNotice(`A password reset link is on its way to ${m.email}.`);
                                else setReset({ member: m, link: r.link, minutes: r.minutes });
                              })
                            }
                          >
                            <Icon name="key" size={16} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            aria-label={`Remove ${m.name || m.email}’s access`}
                            title="Remove access"
                            onClick={() => setRemoving(m)}
                          >
                            <Icon name="trash" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.invites.map((i) => (
                    <tr key={i.id} className="staged">
                      <td>
                        <span className="cell-stack">
                          <span>
                            {i.name || i.email}{' '}
                            <Chip small tone="brand">
                              Invited
                            </Chip>
                          </span>
                          <small className="muted">{i.email}</small>
                        </span>
                      </td>
                      <td>{accessLabel(i, roleName(i.roleId))}</td>
                      <td className="hide-sm muted">Link expires {dateOnly(i.expiresAt)}</td>
                      <td>
                        <div className="row-actions">
                          <Button size="small" variant="ghost" onClick={() => void act(() => api.revokeInvite(d, i.id))}>
                            Cancel invitation
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Roles"
          action={
            <Button size="small" icon="plus" disabled={!data} onClick={() => setRoleEdit('new')}>
              New role
            </Button>
          }
        >
          {roles.length === 0 ? (
            <div className="card-body">
              <Empty title="No roles of your own yet">For example a Queue admin, who can open calls and play recordings for a few call queues, and nothing else.</Empty>
            </div>
          ) : (
            <ul className="role-list card-body">
              {roles.map((r) => {
                const n = data?.members.filter((m) => m.roleId === r.id).length ?? 0;
                return (
                  <li key={r.id}>
                    <span className="cell-stack grow">
                      <strong>{r.name}</strong>
                      <small className="muted">{roleSummary(r, queueName)}</small>
                    </span>
                    <span className="muted">{plural(n, 'person', 'people')}</span>
                    <button type="button" className="icon-btn" aria-label={`Change the ${r.name} role`} title="Change" onClick={() => setRoleEdit(r)}>
                      <Icon name="edit" size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
      {asking && data?.seats && (
        <RequestSeatsDialog
          seats={data.seats}
          onClose={() => setAsking(false)}
          onAsk={async (more, note) => {
            await api.askForSeats(d, more, note);
            setAsking(false);
            access.reload();
          }}
        />
      )}
      {roleEdit && (
        <RoleDialog
          role={roleEdit === 'new' ? null : roleEdit}
          queues={queues}
          holders={
            roleEdit === 'new'
              ? []
              : [
                  ...(data?.members ?? []).filter((m) => m.roleId === roleEdit.id).map((m) => m.name || m.email),
                  ...(data?.invites ?? []).filter((i) => i.roleId === roleEdit.id).map((i) => `${i.name || i.email} (invited)`)
                ]
          }
          onClose={() => setRoleEdit(null)}
          onSave={async (body) => {
            if (roleEdit === 'new') await api.createRole(d, body);
            else await api.updateRole(d, roleEdit.id, body);
            setRoleEdit(null);
            access.reload();
          }}
          {...(roleEdit !== 'new'
            ? {
                onDelete: async () => {
                  await api.deleteRole(d, roleEdit.id);
                  setRoleEdit(null);
                  access.reload();
                }
              }
            : {})}
        />
      )}
      <InviteDialog
        key={inviteKey}
        open={inviting}
        dir={dir.data}
        roles={roles}
        queueName={queueName}
        seats={data?.seats}
        staff={staff}
        mail={data?.mail ?? false}
        onClose={() => setInviting(false)}
        onInvite={async (body) => {
          const r = await api.invite(d, body);
          setResult(r);
          setInviting(false);
          access.reload();
        }}
      />
      <Dialog open={Boolean(reset)} onClose={() => setReset(null)} title={reset ? `Reset ${reset.member.name || reset.member.email}’s password` : ''}>
        {reset?.link && <ResetLink link={reset.link} minutes={reset.minutes} />}
      </Dialog>
      <Dialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${removing.name || removing.email}’s access?` : ''}
        foot={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const m = removing;
                setRemoving(null);
                if (m) void act(() => api.removeMember(d, m.id));
              }}
            >
              Remove access
            </Button>
          </>
        }
      >
        <p>
          {removing ? removing.name || removing.email : ''} is signed out of Stitch at once and can’t sign in again until they’re invited. Their extension, phone and voicemail aren’t
          changed.
        </p>
      </Dialog>
      {editing && (
        <AccessDialog
          member={editing}
          dir={dir.data}
          roles={roles}
          queueName={queueName}
          seats={data?.seats}
          staff={staff}
          newPerson={`/t/${encodeURIComponent(d)}/people/new`}
          onClose={() => setEditing(null)}
          onSave={async (set) => {
            await api.changeMember(d, editing.id, set);
            setEditing(null);
            access.reload();
          }}
        />
      )}
    </>
  );
}

const people = (dir: Directory | undefined): Directory | undefined =>
  dir ? { ...dir, entries: dir.entries.filter((e) => e.kind === 'person' && e.featureSet !== MAILBOX_FEATURE_SET) } : dir;

/** Who they are here: an admin, an end user with their own phone, messaging only (no extension, no role), or one of the company's roles. */
type Pick = { role: 'admin' | 'user'; roleId: string | null; messaging?: boolean };
const MESSAGING: Pick = { role: 'user', roleId: null, messaging: true };
const isMessaging = (p: Pick) => p.role === 'user' && !p.roleId && Boolean(p.messaging);

export const PERMISSION_LABELS: Record<RolePermission, { label: string; help: string }> = {
  calls: { label: 'Call history', help: 'Open calls and see where they went' },
  recordings: { label: 'Call recordings', help: 'Play recorded calls' },
  wallboard: { label: 'Wallboard', help: 'Callers waiting and agents, live' },
  reports: { label: 'Queue reports', help: 'How queues did over time' },
  agents: { label: 'Sign agents in and out', help: 'From the wallboard' },
  // 1.13.0 (3099): for a bookkeeper. Never per queue; on its own, it's all they see.
  billing: { label: 'Billing', help: 'Invoices, the next bill, who gets invoices and the billing address' }
};

/** Permissions that are about call queues (billing is the whole company's). */
const QUEUE_PERMISSIONS: RolePermission[] = ['calls', 'recordings', 'wallboard', 'reports', 'agents'];

export function roleSummary(r: RoleView, queueName: (q: string) => string): string {
  const parts = (Object.entries(r.permissions) as [RolePermission, 'all' | string[]][]).map(
    ([k, v]) => `${PERMISSION_LABELS[k].label}${v === 'all' || k === 'billing' ? '' : ` (${v.map(queueName).join(', ') || 'no queues'})`}`
  );
  return parts.join(' · ') || 'Nothing yet';
}

function RolePicker({
  pick,
  setPick,
  roles,
  queueName,
  seats,
  staff = false,
  holdsSeat = false
}: {
  pick: Pick;
  setPick: (p: Pick) => void;
  roles: RoleView[];
  queueName: (q: string) => string;
  /** Messaging-only people: how many the plan includes, and how many are taken. */
  seats?: MessagingSeats | undefined;
  staff?: boolean;
  /** They already take one (no extension now), so choosing messaging only needs no free one. */
  holdsSeat?: boolean;
}) {
  const is = (p: Pick) => pick.role === p.role && (pick.roleId ?? null) === (p.roleId ?? null) && isMessaging(pick) === isMessaging(p);
  const seatFree = !seats || holdsSeat || seats.used < seats.allowance;
  return (
    <fieldset className="plain-fieldset">
      <legend className="field-label">What they can do</legend>
      <div className="radio-cards">
        <label className="radio-card">
          <input type="radio" name="role" checked={is({ role: 'admin', roleId: null })} onChange={() => setPick({ role: 'admin', roleId: null })} />
          <span className="cell-stack">
            <strong>Admin</strong>
            <small>Manage people, phones, call routing and access</small>
          </span>
        </label>
        <label className="radio-card">
          <input type="radio" name="role" checked={is({ role: 'user', roleId: null })} onChange={() => setPick({ role: 'user', roleId: null })} />
          <span className="cell-stack">
            <strong>Their own phone</strong>
            <small>See their phone’s keys, sign in to queues, see their calls</small>
          </span>
        </label>
        {/* Messaging only: no extension, so nothing is set up on the phone system for them. */}
        <label className={`radio-card${seatFree || is(MESSAGING) ? '' : ' disabled'}`}>
          <input type="radio" name="role" checked={is(MESSAGING)} disabled={!seatFree && !is(MESSAGING)} onChange={() => setPick(MESSAGING)} />
          <span className="cell-stack">
            <strong>Messaging only (no phone)</strong>
            <small>Channels and direct messages in Messages. No extension, so no calls until you give them a phone.</small>
            {seats && <small className={seatFree ? 'muted' : 'seats-full'}>{seatFree ? seatsText(seats) : seatsFullText(seats, staff)}</small>}
          </span>
        </label>
        {roles.map((r) => (
          <label key={r.id} className="radio-card">
            <input type="radio" name="role" checked={is({ role: 'user', roleId: r.id })} onChange={() => setPick({ role: 'user', roleId: r.id })} />
            <span className="cell-stack">
              <strong>{r.name}</strong>
              <small>{roleSummary(r, queueName)}</small>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function InviteDialog({
  open,
  dir,
  mail,
  roles,
  queueName,
  seats,
  staff,
  onClose,
  onInvite
}: {
  open: boolean;
  dir: Directory | undefined;
  mail: boolean;
  roles: RoleView[];
  queueName: (q: string) => string;
  seats: MessagingSeats | undefined;
  staff: boolean;
  onClose: () => void;
  onInvite: (body: { email: string; name: string; role: 'admin' | 'user'; extension?: string; roleId?: string; messagingOnly?: true }) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [pick, setPick] = useState<Pick>({ role: 'user', roleId: null });
  const [extension, setExtension] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // An end user needs their own phone, a role, or both; or is messaging only (no extension at all).
  const messaging = isMessaging(pick);
  const needsExtension = pick.role === 'user' && !pick.roleId && !messaging;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite someone"
      subtitle={mail ? 'They get an email with a link to choose a password.' : 'You’ll get a link to send them.'}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy || !email.trim() || (needsExtension && !extension)}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await onInvite({
                  email: email.trim(),
                  name: name.trim(),
                  role: pick.role,
                  ...(messaging ? { messagingOnly: true as const } : extension ? { extension } : {}),
                  ...(pick.roleId ? { roleId: pick.roleId } : {})
                });
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
                setBusy(false);
              }
            }}
          >
            {mail ? 'Send invitation' : 'Create the link'}
          </Button>
        </>
      }
    >
      <RolePicker pick={pick} setPick={setPick} roles={roles} queueName={queueName} seats={seats} staff={staff} />
      {messaging ? (
        <p className="muted">Nothing is set up on the phone system for them. To give them a phone later, link their extension from this page.</p>
      ) : (
        <DestinationPicker
          label={needsExtension ? 'Their extension' : 'Their extension (optional)'}
          help={pick.role === 'admin' ? 'With one, they can switch between the company admin and their own phone.' : pick.roleId ? 'With one, they also see their own phone.' : undefined}
          mode="people"
          value={extension}
          onChange={(v) => {
            setExtension(v);
            const e = dir?.entries.find((x) => x.extension === v);
            if (e && !name) setName(e.name);
          }}
          dir={people(dir)}
          clearable={!needsExtension}
        />
      )}
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-autofocus />
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} hint="They can change it when they accept." />
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

function AccessDialog({
  member,
  dir,
  roles,
  queueName,
  seats,
  staff,
  newPerson,
  onClose,
  onSave
}: {
  member: MemberView;
  dir: Directory | undefined;
  roles: RoleView[];
  queueName: (q: string) => string;
  seats: MessagingSeats | undefined;
  staff: boolean;
  /** People → Add a person, for someone who has no extension on the phone system yet. */
  newPerson: string;
  onClose: () => void;
  onSave: (set: { role: 'admin' | 'user'; extension: string | null; roleId: string | null; messagingOnly?: true }) => Promise<void>;
}) {
  const [pick, setPick] = useState<Pick>(member.messagingOnly ? MESSAGING : { role: member.role, roleId: member.roleId ?? null });
  const [extension, setExtension] = useState(member.extension ?? '');
  const [error, setError] = useState('');
  const messaging = isMessaging(pick);
  const needsExtension = pick.role === 'user' && !pick.roleId && !messaging;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${member.name || member.email}’s access`}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={needsExtension && !extension}
            onClick={async () => {
              setError('');
              try {
                await onSave(messaging ? { role: 'user', extension: null, roleId: null, messagingOnly: true } : { role: pick.role, extension: extension || null, roleId: pick.roleId });
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <RolePicker pick={pick} setPick={setPick} roles={roles} queueName={queueName} seats={seats} staff={staff} holdsSeat={member.role === 'user' && !member.extension} />
      {messaging ? (
        // Giving them a phone later: link an extension that's already on the phone system.
        <div className="section-stack">
          <DestinationPicker
            label="Give them a phone: their extension"
            mode="people"
            value=""
            onChange={(v) => {
              if (!v) return;
              setExtension(v);
              setPick({ role: 'user', roleId: null });
            }}
            dir={people(dir)}
            clearable={false}
          />
          <p className="muted">
            No extension for them yet? <Link to={newPerson}>Add them in People</Link> (that sets up their phone), then link it here.
          </p>
        </div>
      ) : (
        <DestinationPicker label={needsExtension ? 'Their extension' : 'Their extension (optional)'} mode="people" value={extension} onChange={setExtension} dir={people(dir)} />
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}

/** Make or change one of the company's roles: a name, and what it may do, for which queues. */
function RoleDialog({
  role,
  queues,
  holders = [],
  onClose,
  onSave,
  onDelete
}: {
  role: RoleView | null;
  queues: { extension: string; name: string }[];
  /** Who has this role, or is invited with it: while anyone does, it can't be deleted (3140). */
  holders?: string[];
  onClose: () => void;
  onSave: (body: { name: string; permissions: RolePermissions }) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [name, setName] = useState(role?.name ?? '');
  const [perms, setPerms] = useState<RolePermissions>(role?.permissions ?? { calls: 'all', recordings: 'all' });
  const [error, setError] = useState('');
  const keys = Object.keys(PERMISSION_LABELS) as RolePermission[];
  // One choice of queues for the whole role keeps it simple; a permission without queues is left out.
  const firstScope = QUEUE_PERMISSIONS.map((k) => perms[k]).find((v) => v !== undefined);
  const [scope, setScope] = useState<'all' | string[]>(firstScope ?? 'all');
  const toggle = (k: RolePermission) =>
    setPerms((p) => (p[k] === undefined ? { ...p, [k]: k === 'billing' ? 'all' : scope } : Object.fromEntries(Object.entries(p).filter(([x]) => x !== k))));
  const setAllScopes = (next: 'all' | string[]) => {
    setScope(next);
    setPerms((p) => Object.fromEntries(Object.keys(p).map((k) => [k, k === 'billing' ? 'all' : next])) as RolePermissions);
  };
  // Queues matter only when the role has something about calls.
  const aboutQueues = QUEUE_PERMISSIONS.some((k) => perms[k] !== undefined);
  const chosen = scope === 'all' ? null : new Set(scope);
  return (
    <Dialog
      open
      onClose={onClose}
      title={role ? `Change ${role.name}` : 'New role'}
      subtitle="People with this role see only what you tick here, for the queues you choose."
      foot={
        <>
          {onDelete && (
            <Button
              variant="danger"
              disabled={holders.length > 0}
              title={holders.length ? 'Give everyone with this role another one first' : undefined}
              onClick={async () => {
                setError('');
                try {
                  await onDelete();
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                }
              }}
            >
              Delete role
            </Button>
          )}
          <span className="grow" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || Object.keys(perms).length === 0 || (aboutQueues && scope !== 'all' && scope.length === 0)}
            onClick={async () => {
              setError('');
              try {
                await onSave({ name: name.trim(), permissions: perms });
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Save role
          </Button>
        </>
      }
    >
      {onDelete && holders.length > 0 && (
        <Notice title="This role is in use">
          {new Intl.ListFormat('en', { type: 'conjunction' }).format(holders)} {holders.length === 1 ? 'has' : 'have'} it. To delete it, give{' '}
          {holders.length === 1 ? 'them' : 'each of them'} another role first, so nobody loses pages without knowing.
        </Notice>
      )}
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Queue admin" data-autofocus />
      <fieldset className="plain-fieldset">
        <legend className="field-label">What they can see and do</legend>
        <div className="check-list">
          {keys.map((k) => (
            <label key={k} className="check-row">
              <input type="checkbox" checked={perms[k] !== undefined} onChange={() => toggle(k)} />
              <span className="cell-stack">
                <strong>{PERMISSION_LABELS[k].label}</strong>
                <small className="muted">{PERMISSION_LABELS[k].help}</small>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      {aboutQueues && (
      <fieldset className="plain-fieldset">
        <legend className="field-label">For which call queues</legend>
        <div className="segmented" role="radiogroup" aria-label="Queues">
          <label className={scope === 'all' ? 'on' : ''}>
            <input type="radio" name="scope" checked={scope === 'all'} onChange={() => setAllScopes('all')} />
            Every call queue
          </label>
          <label className={scope !== 'all' ? 'on' : ''}>
            <input type="radio" name="scope" checked={scope !== 'all'} onChange={() => setAllScopes([])} />
            Only some
          </label>
        </div>
        {chosen && (
          <div className="check-list">
            {queues.length === 0 && <p className="muted">This company has no call queues yet.</p>}
            {queues.map((q) => (
              <label key={q.extension} className="check-row">
                <input
                  type="checkbox"
                  checked={chosen.has(q.extension)}
                  onChange={() => setAllScopes(chosen.has(q.extension) ? [...chosen].filter((x) => x !== q.extension) : [...chosen, q.extension])}
                />
                <span>
                  {q.name} <span className="muted mono">{q.extension}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        {/* Says what each choice really shows (3138): Every call queue opens the whole company's call history. */}
        <small className="muted">
          {scope === 'all'
            ? 'Call history and recordings: every call at your company, not only queue calls. Reports and the wallboard: every queue.'
            : 'Call history and recordings: only calls that went to these queues. Reports and the wallboard: only these queues.'}
        </small>
      </fieldset>
      )}
      {perms.billing !== undefined && Object.keys(perms).length === 1 && (
        <small className="muted">With only Billing, people see Billing and their own settings: no phones, calls or messages. Good for a bookkeeper.</small>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}
